import { useEffect, useState } from 'react';
import { useI18n, type CopyKey } from './i18n';
import { getWindow, isError, saveWindow, setPaused as savePaused } from './api';
import { reachableMinutes } from '@nabu/shared/time';
import type { ReachWindow } from '@nabu/shared';

/**
 * How often the learner is reached, and when they are not.
 *
 * Three controls and one sentence, and the sentence is the point. "Six" beside
 * "08:00" beside "21:00" is three facts a learner has to combine in their head to
 * find out what they have agreed to; the panel does that combining out loud, so
 * choosing badly is visible before saving rather than at three in the morning.
 *
 * It belongs to the learner rather than to an objective, so it is rendered on the
 * objectives screen whether or not one is running — the schedule is a standing
 * arrangement and closing an objective does not end it.
 *
 * The imported `reachableMinutes` is the only piece of shared logic pulled in
 * rather than restated. It comes from the `@nabu/shared/time` subpath deliberately:
 * the package root re-exports the scheduler, which would drag `ts-fsrs` and `yaml`
 * into a bundle loaded over a phone connection for the sake of one subtraction.
 */

/** The bounds the shared validator enforces. Offered here so the API never has to refuse. */
const MIN_LIMIT = 1;
const MAX_LIMIT = 24;

/** Which copy explains a refusal, by the `reason` the API sent with it. */
const PROBLEM_COPY: Record<string, CopyKey> = {
  limit_out_of_range: 'win.error.limit',
  limit_not_whole: 'win.error.limitWhole',
  time_unreadable: 'win.error.time',
  window_empty: 'win.error.empty',
  window_wraps_midnight: 'win.error.wrap',
  malformed: 'win.error.generic',
};

export default function Schedule() {
  const { t } = useI18n();
  /*
   * `undefined` until the stored window has been read, and the panel renders nothing
   * but a line of copy until then. Starting from the defaults instead would show a
   * schedule the learner never chose and let them save it by pressing the button
   * before their real one had arrived.
   */
  const [draft, setDraft] = useState<ReachWindow | undefined>();
  const [timezone, setTimezone] = useState<string | undefined>();
  const [configured, setConfigured] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [problem, setProblem] = useState<string | undefined>();
  const [unreadable, setUnreadable] = useState(false);
  const [paused, setPausedState] = useState(false);
  const [pausedReason, setPausedReason] = useState<'asked' | 'no_answer' | undefined>();
  const [pausing, setPausing] = useState(false);

  /*
   * Asked once, and deliberately depending on nothing.
   *
   * `t` is a new closure on every language change, so listing it here would refetch
   * on a tap of EN/ES — and the fetch replaces `draft`, which would silently discard
   * hours the learner had adjusted and not yet saved. Which is why the failure is
   * kept as a flag and turned into copy at render time instead.
   */
  useEffect(() => {
    void getWindow().then((result) => {
      if (isError(result)) {
        // An unreadable window is left as nothing rather than as the defaults: the
        // learner may already have a schedule running, and offering them a form
        // pre-filled with 08:00–21:00 invites them to overwrite it with a guess.
        setUnreadable(true);
        return;
      }
      setDraft(result.window);
      setTimezone(result.timezone);
      setConfigured(result.configured);
      setPausedState(result.paused);
      setPausedReason(result.pausedReason);
    });
  }, []);

  /**
   * Copy with the learner's own numbers in it.
   *
   * Local to this screen rather than folded into `t()`, because this is the only
   * place whose sentences have to name values the learner picked — and Spanish puts
   * them in a different order, so composing the sentence out of JSON fragments in
   * the component would produce one language reading correctly and the other not.
   */
  function fill(key: CopyKey, values: Record<string, string>): string {
    return t(key).replace(/\{(\w+)\}/g, (_, name: string) => values[name] ?? '');
  }

  /**
   * Roughly how far apart the messages land, spelled out.
   *
   * An illustration and not a promise, and deliberately hedged in the copy as
   * "about": the cap is a ceiling rather than a metronome, and what actually decides
   * the moment is what has come due — `nextSendTime` in the shared package is the
   * authority. Dividing the window by the cap is the honest way to answer "so how
   * often is that", which is the question a bare "6" does not.
   */
  function spacing(window: ReachWindow): string {
    const gap = Math.round(reachableMinutes(window) / window.dailyLimit);
    const hours = Math.floor(gap / 60);
    const minutes = gap % 60;
    if (hours === 0) return `${minutes} ${t('win.unit.minutes')}`;
    if (minutes === 0) return `${hours} ${t('win.unit.hours')}`;
    return `${hours} ${t('win.unit.hours')} ${minutes} ${t('win.unit.minutes')}`;
  }

  async function save() {
    if (!draft) return;
    setBusy(true);
    setSaved(false);
    setProblem(undefined);

    const result = await saveWindow(draft);
    setBusy(false);
    if (isError(result)) {
      if (result.error === 'sign_in_required') {
        setProblem(t('obj.error.signIn'));
        return;
      }
      // The reason names which control was wrong. Without it this reads "that did
      // not work" beside three inputs, one of which is at fault.
      const copy = result.reason ? PROBLEM_COPY[result.reason] : undefined;
      setProblem(t(copy ?? 'win.error.generic'));
      return;
    }

    // Believed over what was asked for: the API returns the window it actually
    // stored, in canonical form, and that is what the sweep will read.
    setDraft(result.window);
    setTimezone(result.timezone);
    setConfigured(true);
    setSaved(true);
  }

  /** One field at a time, on a copy, so a change to the hours cannot lose the cap. */
  function change(patch: Partial<ReachWindow>) {
    setSaved(false);
    setDraft((current) => (current ? { ...current, ...patch } : current));
  }

  /**
   * Stopping or restarting the messages.
   *
   * Optimistic, and reverted on failure. A switch that waits for a round trip before
   * moving reads as broken, and this one is most likely to be reached for by somebody
   * who wants the messages to stop *now*.
   */
  async function togglePaused(next: boolean) {
    setPausing(true);
    setProblem(undefined);
    setPausedState(next);
    const result = await savePaused(next);
    setPausing(false);
    if (isError(result)) {
      setPausedState(!next);
      setProblem(t('win.pause.failed'));
      return;
    }
    // The reason only means anything while paused, and after the learner has just
    // chosen it themselves, "you stopped replying" would be a lie.
    setPausedReason(next ? 'asked' : undefined);
  }

  return (
    <section className="panel">
      <h2>{t('win.title')}</h2>
      <p className="muted">{t('win.hint')}</p>

      {/*
       * Pausing reads before the hours, because somebody looking for it wants it now.
       *
       * And the two reasons are not one message: `no_answer` happened *to* them after
       * three unanswered reminders, and telling somebody who never touched the switch
       * that they had turned it off would be both confusing and slightly insulting.
       */}
      <div className="field">
        <label className="switch">
          <input
            type="checkbox"
            checked={!paused}
            disabled={pausing}
            onChange={(e) => void togglePaused(!e.target.checked)}
          />
          <span>{t('win.pause.label')}</span>
        </label>
        <small>
          {paused
            ? pausedReason === 'no_answer'
              ? t('win.pause.stoppedBySilence')
              : t('win.pause.stopped')
            : t('win.pause.running')}
        </small>
      </div>

      {!draft ? (
        <p className="muted">{unreadable ? t('win.error.load') : t('win.loading')}</p>
      ) : (
        <div className="form">
          {!configured && <p className="muted small">{t('win.defaults')}</p>}

          <label className="field">
            <span>{t('win.limit.label')}</span>
            <input
              type="range"
              min={MIN_LIMIT}
              max={MAX_LIMIT}
              value={draft.dailyLimit}
              onChange={(e) => change({ dailyLimit: Number(e.target.value) })}
              disabled={busy}
            />
            <small>
              {draft.dailyLimit} {t('win.limit.unit')}
            </small>
          </label>

          {/* A fieldset rather than a label, because two inputs cannot share one. */}
          <fieldset className="field">
            <span>{t('win.hours.label')}</span>
            <div className="hours">
              <label className="hour">
                <small>{t('win.hours.from')}</small>
                <input
                  type="time"
                  value={draft.reachFrom}
                  onChange={(e) => change({ reachFrom: e.target.value })}
                  disabled={busy}
                  required
                />
              </label>
              <label className="hour">
                <small>{t('win.hours.until')}</small>
                <input
                  type="time"
                  value={draft.reachUntil}
                  onChange={(e) => change({ reachUntil: e.target.value })}
                  disabled={busy}
                  required
                />
              </label>
            </div>
            <small>{t('win.hours.hint')}</small>
          </fieldset>

          <div className="field">
            <label className="switch">
              <input
                type="checkbox"
                checked={draft.explanations}
                onChange={(e) => change({ explanations: e.target.checked })}
                disabled={busy}
              />
              <span>{t('win.explanations.label')}</span>
            </label>
            <small>{t('win.explanations.hint')}</small>
          </div>

          {/*
           * What the three controls above add up to, before it is saved.
           *
           * Recomputed from the draft on every keystroke rather than from what is
           * stored, because its whole job is to let somebody see the consequence of
           * a change while they are still making it.
           */}
          <p className="plain">
            <span>
              {draft.dailyLimit === 1
                ? fill('win.plain.one', { from: draft.reachFrom, until: draft.reachUntil })
                : fill('win.plain', {
                    count: String(draft.dailyLimit),
                    from: draft.reachFrom,
                    until: draft.reachUntil,
                    gap: spacing(draft),
                  })}
            </span>
            <span>{fill('win.plain.quiet', { from: draft.reachFrom, until: draft.reachUntil })}</span>
            <span>
              {t(draft.explanations ? 'win.plain.explanations.on' : 'win.plain.explanations.off')}
            </span>
          </p>

          {/*
           * Said out loud when it is missing, rather than left to be discovered.
           *
           * Hours without a zone are not a schedule — read as UTC, an 08:00 start is
           * two in the morning here — so this is a refusal to pretend rather than a
           * warning about a detail.
           */}
          {timezone ? (
            <p className="muted small">
              {t('win.zone')} <strong>{timezone}</strong>
            </p>
          ) : (
            <p className="problem" role="alert">
              {t('win.zone.missing')}
            </p>
          )}

          {problem && (
            <p className="problem" role="alert">
              {problem}
            </p>
          )}

          <div className="actions">
            <button
              type="button"
              className="button primary"
              onClick={() => void save()}
              disabled={busy}
            >
              {busy ? t('win.working') : t('win.save')}
            </button>
            {saved && (
              <span className="muted" role="status">
                {t('win.saved')}
              </span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
